import React, { useMemo, useState } from "react";
import { Form, FormSpy } from "react-final-form";
import { Alert, Box, Button, Tabs } from "@mui/material";
import { FormApi } from "final-form";
import { defer, isEqual } from "lodash";
import { enqueueSnackbar } from "notistack";

import { LoadingButton } from "ui";

import { DEFAULT_MU_POWER_ARCHITECTURE } from "fond/project/panels/MuPower/architecture/constants";
import { getTabsWithErrors, toFormValues, toPowerArchitecture } from "fond/project/panels/MuPower/architecture/helpers";
import MuPowerArchitectureTabPanel from "fond/project/panels/MuPower/architecture/MuPowerArchitectureTabPanel";
import PrimaryWiresTab from "fond/project/panels/MuPower/architecture/PrimaryWiresTab";
import SecondaryWiresTab from "fond/project/panels/MuPower/architecture/SecondaryWiresTab";
import TransformersTab from "fond/project/panels/MuPower/architecture/TransformersTab";
import { MuPowerArchitectureFormValues } from "fond/project/panels/MuPower/architecture/types";
import { PowerArchitecture, SystemOfMeasurement } from "fond/types";
import { ConfirmModal, Modal } from "fond/widgets";

import { ArchitectureForm, ArchitectureTab, MultiUtilityChip } from "./MuPowerArchitectureModal.styles";

const FORM_ID = "edit-mu-power-architecture-form";
const TAB_LABELS = ["Transformers", "Primary", "Secondary"];

function a11yTabProps(id: number) {
  return {
    id: `mu-power-architecture-tab-${id}`,
    "aria-controls": `mu-power-architecture-tabpanel-${id}`,
  };
}

interface MuPowerArchitectureModalProps {
  powerArchitecture: PowerArchitecture | null;
  systemOfMeasurement: SystemOfMeasurement;
  onClose: () => void;
  onSave: (powerArchitecture: PowerArchitecture) => Promise<void>;
}

const MuPowerArchitectureModal: React.FC<MuPowerArchitectureModalProps> = ({ powerArchitecture, systemOfMeasurement, onClose, onSave }) => {
  let formApi: FormApi<MuPowerArchitectureFormValues> | null = null;
  const [isSaving, setIsSaving] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [tabsWithErrors, setTabsWithErrors] = useState<number[]>([]);
  const [confirmCloseWithoutSaving, setConfirmCloseWithoutSaving] = useState(false);

  const isLastTab = selectedTab === TAB_LABELS.length - 1;

  const initialValues = useMemo(
    () => toFormValues(powerArchitecture ?? DEFAULT_MU_POWER_ARCHITECTURE, systemOfMeasurement),
    [powerArchitecture, systemOfMeasurement]
  );

  const onCloseHandler = () => {
    // Don't allow the modal to be closed while the save operation is in progress
    if (isSaving) return;

    if (formApi?.getState().dirty) {
      setConfirmCloseWithoutSaving(true);
    } else {
      onClose();
    }
  };

  const onSubmit = async (values: MuPowerArchitectureFormValues) => {
    setIsSaving(true);
    try {
      await onSave(toPowerArchitecture(values, systemOfMeasurement));
      onClose();
    } catch {
      enqueueSnackbar("Failed to save the architecture. Please try again.", { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const updateTabErrors = () => {
    // defer to allow the form values to propagate to the form state.
    defer(() => {
      if (!formApi || !formApi.getState().submitFailed) return;

      const errorTabs = getTabsWithErrors(formApi.getState().errors);
      setTabsWithErrors((previous) => (isEqual(previous, errorTabs) ? previous : errorTabs));
    });
  };

  const modalContent = (
    <Form<MuPowerArchitectureFormValues>
      initialValues={initialValues}
      keepDirtyOnReinitialize
      onSubmit={onSubmit}
      // We use an empty subscription to increase form performance - preventing
      // the form from re-rendering every time a value changes
      // See: https://final-form.org/docs/react-final-form/examples/subscriptions
      subscription={{}}
      render={({ handleSubmit, form }) => {
        formApi = form;

        const onFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
          handleSubmit(event);
          // defer to allow the submission state to settle before inspecting errors.
          defer(() => {
            const { hasValidationErrors, errors } = form.getState();
            if (!hasValidationErrors) return;

            const errorTabs = getTabsWithErrors(errors);
            setTabsWithErrors(errorTabs);
            if (errorTabs.length > 0 && !errorTabs.includes(selectedTab)) {
              setSelectedTab(errorTabs[0]);
            }
          });
        };

        return (
          <ArchitectureForm id={FORM_ID} onSubmit={onFormSubmit}>
            <FormSpy subscription={{ errors: true }} onChange={updateTabErrors} />
            <Tabs value={selectedTab} onChange={(_, newValue) => setSelectedTab(newValue)}>
              {TAB_LABELS.map((label, index) => (
                <ArchitectureTab
                  key={label}
                  className={tabsWithErrors.includes(index) ? "validation-error" : ""}
                  label={`${label}${tabsWithErrors.includes(index) ? "*" : ""}`}
                  {...a11yTabProps(index)}
                />
              ))}
            </Tabs>
            <MuPowerArchitectureTabPanel value={selectedTab} id={0}>
              <TransformersTab />
            </MuPowerArchitectureTabPanel>
            <MuPowerArchitectureTabPanel value={selectedTab} id={1}>
              <PrimaryWiresTab />
            </MuPowerArchitectureTabPanel>
            <MuPowerArchitectureTabPanel value={selectedTab} id={2}>
              <SecondaryWiresTab systemOfMeasurement={systemOfMeasurement} />
            </MuPowerArchitectureTabPanel>
          </ArchitectureForm>
        );
      }}
    />
  );

  return (
    <>
      <ConfirmModal
        open={confirmCloseWithoutSaving}
        content="Are you sure you want to discard your changes?"
        onConfirm={() => {
          setConfirmCloseWithoutSaving(false);
          onClose();
        }}
        onCancel={() => setConfirmCloseWithoutSaving(false)}
      />
      <Modal
        open
        header={
          <Box display="flex" alignItems="center" gap={1.5}>
            Architecture
            <MultiUtilityChip label="MULTI UTILITY" />
          </Box>
        }
        content={modalContent}
        onClose={onCloseHandler}
        showFullscreen
        showClose
        PaperProps={{ style: { width: "80vw", maxWidth: "1100px", minHeight: "min(700px, 80vh)" } }}
        data-testid="edit-mu-power-architecture-modal"
        actions={
          <>
            {isLastTab && tabsWithErrors.length > 0 && (
              <Alert severity="error" sx={{ py: 0, mr: 1 }}>
                Some fields need attention before the architecture can be saved.
              </Alert>
            )}
            <Button
              data-testid="edit-mu-power-architecture-cancel-button"
              color="primary"
              disabled={isSaving}
              onClick={onCloseHandler}
              sx={{ marginRight: 1 }}
            >
              Cancel
            </Button>
            {isLastTab ? (
              <LoadingButton data-testid="edit-mu-power-architecture-save-button" color="primary" loading={isSaving} type="submit" form={FORM_ID}>
                {powerArchitecture ? "Update" : "Save"}
              </LoadingButton>
            ) : (
              <Button
                data-testid="edit-mu-power-architecture-next-button"
                color="primary"
                variant="contained"
                onClick={() => setSelectedTab(selectedTab + 1)}
              >
                Next
              </Button>
            )}
          </>
        }
      />
    </>
  );
};

export default MuPowerArchitectureModal;
